import { BASEURL } from "../BaseUrl";

/**
 * Fetches a list of instruments with optional filters and pagination.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to filter and paginate the instrument list.
 * @param {string} data.user_id - The ID of the user requesting the instrument list.
 * @param {string} data.search - The search term to filter instruments by name or other attributes.
 * @param {string} data.searchParam - The parameter to search by (e.g., name).
 * @param {number} data.pageNumber - The page number for pagination.
 * @param {number} data.pageSize - The number of instruments per page for pagination.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getAllInstruments = async (axios, data) => {
  return await axios.get(`${BASEURL}/instrument/get-instruments`, {
    params: {
      client_id: data.client_id ?? "",
      user_id: data.user_id,
      search: data.search,
      searchParam: data.searchParam,
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
      pagination:data.pagination,
    },
  });
};

/**
 * Creates a new instrument with the provided details.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} userId - The ID of the user creating the instrument.
 * @param {string} username - The username of the user creating the instrument.
 * @param {string} instrumentName - The name of the new instrument.
 * @param {string} fileType - The type of file associated with the instrument.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createInstrument = async (axios, data) => {
  return await axios.post(`${BASEURL}/instrument/add-instrument`, {
    user_id: data.user_id,
    username: data.username,
    instrument_name: data.instrument_name.toUpperCase(),
    domain_id: data.domain_id,
    client_id: data.client_id,
    file_type: data.file_type,
  });
};

/**
 * Deletes a specific instrument by ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} instrument_id - The ID of the instrument to be deleted.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteInstrument = async (axios, instrument_id) => {
  return await axios.delete(`${BASEURL}/instrument/delete-instrument`, {
    params: { instrument_id },
  });
};

/**
 * Fetches a specific instrument by ID and name.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} instrumentId - The ID of the instrument to fetch.
 * @param {string} instrumentName - The name of the instrument to fetch.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getInstrumentById = async (axios, instrumentId, instrumentName) => {
  return await axios.get(`${BASEURL}/instrument/get-instrument-by-id`, {
    params: { instrumentId: instrumentId, instrumentName: instrumentName },
  });
};


/**
 * Fetches all instruments associated with a specific client ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_id - The ID of the client to fetch instruments for.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getInstrumentsByClientId = async (axios, client_id) => {
  return await axios.get(`${BASEURL}/instrument/get-instruments-by-client-id`, {
    params: { clientId: client_id },
  });
};



/**
 * Sends data to the API for a specific instrument.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {FormData} formData - The form data to be uploaded.
 * @param {string} instrumentId - The ID of the instrument associated with the data.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const sendDataToAPI = async (axios, formData, instrumentId) => {
  return await axios.post(`${BASEURL}/eachInstrument/upload-csv`, formData, {
    params: {
      instrumentId: instrumentId,
    },
  });
};

/**
 * Fetches data for a specific instrument.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} instrumentId - The ID of the instrument to fetch.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getEachInstrumentById = async (axios, instrumentId) => {
  return await axios.get(`${BASEURL}/eachInstrument/get-instruments`, {
    params: { instrumentId: instrumentId },
  });
};

/**
 * check if a specific instrument is currently used in any recent workflows.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} instrumentName - The ID of the instrument to fetch.
 */
const checkInstrumentUsage = async (axios, instrumentName) => {
  return await axios.post(`${BASEURL}/instrument/check-usage`, null, {
    params: { instrumentName: instrumentName },
  });
};

export const InstrumentApi = {
  getAllInstruments,
  createInstrument,
  deleteInstrument,
  getInstrumentById,
  sendDataToAPI,
  getEachInstrumentById,
  checkInstrumentUsage,
  getInstrumentsByClientId,
};
